/**
 * BulkEaFromSheetModal
 * ---------------------------------------------------------------------------
 * "BULK EA (Via Excel Sheet)" workflow for the Institution portal.
 * Three phases in one modal:
 *   1. UPLOAD   — staff picks a .csv or .xlsx with `Student Name, Email`
 *   2. PREVIEW  — backend cross-references each row (email + name BOTH must
 *                 match) and returns matched / ambiguous / unmatched buckets;
 *                 staff sees counts and can drill into each list.
 *   3. SENDING  — kicks off the async EA send job and polls progress until
 *                 done, then shows the results (per-student sent/skipped).
 *
 * Compliance-gate is intentionally bypassed on the backend because the
 * college has manually vetted the list before uploading. Staff can't
 * accidentally send from the ambiguous or unmatched lists — those rows
 * are read-only in this modal by design.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  UploadSimple,
  CircleNotch,
  CheckCircle,
  WarningCircle,
  XCircle,
  CaretDown,
  CaretRight,
  ArrowLeft,
  PaperPlaneRight,
  FileArrowDown,
  User,
  Calendar,
  GraduationCap,
  Files,
  X,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BulkEaFromSheetModal({ open, onClose, onSent }) {
  const [phase, setPhase] = useState('upload'); // upload | preview | sending | done
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [match, setMatch] = useState(null); // response from bulk-from-csv
  const [expandedList, setExpandedList] = useState(null); // 'matched' | 'ambiguous' | 'unmatched'
  const [sendJob, setSendJob] = useState(null);
  // Ambiguous rows the registrar has eyeballed and confirmed. Shape:
  //   { [row_number]: student_id }
  // Presence of a key = verified; value = which candidate to send to.
  // Cleared on file re-upload so a stale tick can't leak across sessions.
  const [verifiedRows, setVerifiedRows] = useState({});
  // Candidate profile cache for the "click name → see profile" flow.
  //   { [student_id]: { state: 'loading' | 'ready' | 'error', profile?, error? } }
  // We fetch lazily on first click and never refetch — the modal is short-lived.
  const [candidateProfiles, setCandidateProfiles] = useState({});
  // Which candidate is currently expanded inline (only one at a time keeps
  // the UI compact — showing 5 profile cards simultaneously would blow past
  // the modal's height on a laptop screen).
  const [previewCandidateId, setPreviewCandidateId] = useState(null);

  const openCandidatePreview = async (studentId) => {
    // Toggle: click the same candidate again to close.
    if (previewCandidateId === studentId) {
      setPreviewCandidateId(null);
      return;
    }
    setPreviewCandidateId(studentId);
    if (candidateProfiles[studentId]?.state === 'ready') return; // already fetched
    setCandidateProfiles(prev => ({ ...prev, [studentId]: { state: 'loading' } }));
    try {
      const { data } = await axios.get(`${API}/institution/students/${studentId}`);
      setCandidateProfiles(prev => ({ ...prev, [studentId]: { state: 'ready', profile: data } }));
    } catch (err) {
      setCandidateProfiles(prev => ({
        ...prev,
        [studentId]: { state: 'error', error: err?.response?.data?.detail || 'Could not load profile' },
      }));
    }
  };

  const reset = () => {
    setPhase('upload');
    setFile(null);
    setUploading(false);
    setMatch(null);
    setExpandedList(null);
    setSendJob(null);
    setVerifiedRows({});
    setCandidateProfiles({});
    setPreviewCandidateId(null);
  };

  const handleClose = () => {
    if (uploading || phase === 'sending') return; // block close mid-flight
    reset();
    onClose?.();
  };

  const handleFile = async (evt) => {
    const f = evt.target.files?.[0];
    if (!f) return;
    const lower = f.name.toLowerCase();
    if (!(lower.endsWith('.csv') || lower.endsWith('.xlsx') || lower.endsWith('.xlsm'))) {
      toast.error('Please upload a .csv or .xlsx file');
      return;
    }
    setFile(f);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await axios.post(`${API}/institution/ea/bulk-from-csv`, fd);
      setMatch(data);
      setPhase('preview');
      // Auto-expand the biggest actionable bucket first — usually "matched".
      if (data.counts.ambiguous > 0 && data.counts.matched === 0) setExpandedList('ambiguous');
      else if (data.counts.unmatched > 0 && data.counts.matched === 0) setExpandedList('unmatched');
      else setExpandedList('matched');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not parse the file');
      setFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleSend = async () => {
    if (!match?.matched?.length && Object.keys(verifiedRows).length === 0) return;
    // Merge matched student_ids + registrar-verified ambiguous student_ids.
    // Set dedupes when the same person shows up in both buckets (rare but
    // possible if the sheet has a strict-match row AND a typo row for the
    // same student).
    const matchedIds = match.matched.map(m => m.student_id);
    const verifiedIds = Object.values(verifiedRows).filter(Boolean);
    const uniqueIds = [...new Set([...matchedIds, ...verifiedIds])];
    if (!uniqueIds.length) return;

    // Chunked send — DocuSign / Gemini rate-limits kill big batches, and
    // one hung student stalls everything downstream. Fire in groups of
    // CHUNK_SIZE so the user sees steady progress even for 100+ students.
    // Server-side dedupe guard means it's safe to include IDs from any
    // bucket — students who already have an EA are skipped, not
    // re-sent.
    const CHUNK_SIZE = 10;
    const chunks = [];
    for (let i = 0; i < uniqueIds.length; i += CHUNK_SIZE) {
      chunks.push(uniqueIds.slice(i, i + CHUNK_SIZE));
    }

    setPhase('sending');
    setSendJob({
      state: 'running',
      total: uniqueIds.length,
      done: 0,
      counts: { sent: 0, skipped: 0, errors: 0 },
      chunk_index: 0,
      chunk_total: chunks.length,
      results: [],
    });

    let overall = { sent: 0, skipped: 0, errors: 0, results: [] };

    for (let ci = 0; ci < chunks.length; ci += 1) {
      const chunk = chunks[ci];
      let kicked;
      try {
        const { data } = await axios.post(`${API}/institution/ea/bulk-from-csv/send-jobs`, {
          student_ids: chunk,
          source_label: `Bulk EA via sheet: ${file?.name || 'upload'} (chunk ${ci + 1}/${chunks.length})${verifiedIds.length ? ` (+ ${verifiedIds.length} verified)` : ''}`,
        });
        kicked = data;
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Chunk ${ci + 1}/${chunks.length} failed to start`);
        setPhase('preview');
        return;
      }

      // Poll this chunk until done.
      const deadline = Date.now() + 5 * 60 * 1000; // 5 min per chunk
      let finished = null;
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 1500));
        try {
          const { data: s } = await axios.get(`${API}/institution/ea/bulk-from-csv/send-jobs/${kicked.job_id}`);
          // Compose the "overall" view: previous chunks + this chunk's live counts.
          const liveCounts = s.counts || {};
          setSendJob({
            state: 'running',
            total: uniqueIds.length,
            done: overall.sent + overall.skipped + overall.errors + (s.done || 0),
            counts: {
              sent: overall.sent + (liveCounts.sent || 0),
              skipped: overall.skipped + (liveCounts.skipped || 0),
              errors: overall.errors + (liveCounts.errors || 0),
            },
            current_student: s.current_student || '',
            chunk_index: ci + 1,
            chunk_total: chunks.length,
          });
          if (s.state === 'done') { finished = s; break; }
          if (s.state === 'failed') {
            toast.error(`Chunk ${ci + 1}/${chunks.length} failed: ${s.error || 'unknown error'}`);
            setPhase('preview');
            return;
          }
        } catch (_pollErr) {
          // Ignore transient errors — keep polling until deadline
        }
      }
      if (!finished) {
        toast.error(`Chunk ${ci + 1}/${chunks.length} timed out — please retry`);
        setPhase('preview');
        return;
      }
      const c = finished.counts || {};
      overall = {
        sent: overall.sent + (c.sent || 0),
        skipped: overall.skipped + (c.skipped || 0),
        errors: overall.errors + (c.errors || 0),
        results: [...overall.results, ...(finished.results || [])],
      };

      // Tiny pause between chunks so we don't hammer DocuSign back-to-back.
      if (ci < chunks.length - 1) {
        await new Promise(r => setTimeout(r, 500));
      }
    }

    setSendJob({
      state: 'done',
      total: uniqueIds.length,
      done: uniqueIds.length,
      counts: { sent: overall.sent, skipped: overall.skipped, errors: overall.errors },
      results: overall.results,
      chunk_index: chunks.length,
      chunk_total: chunks.length,
    });
    toast.success(`${overall.sent} EA${overall.sent === 1 ? '' : 's'} sent · ${overall.skipped} skipped · ${overall.errors} errors`);
    setPhase('done');
    onSent?.();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl bg-white" data-testid="bulk-ea-sheet-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <FileArrowDown size={20} weight="duotone" className="text-indigo-600" />
            BULK EA (Via Excel Sheet)
          </DialogTitle>
        </DialogHeader>

        {/* ============= UPLOAD PHASE ============= */}
        {phase === 'upload' && (
          <div className="py-4 space-y-4">
            <p className="text-[13px] leading-relaxed text-gray-600">
              Upload the college's approved student list. We'll cross-reference every row by <span className="font-semibold">both name and email</span> and tick only the exact matches — no wrong-student EAs.
            </p>
            <p className="text-[12px] text-gray-500">
              Expected columns: <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">Student Name, Email</span> (matches the "Ready to sent.csv" format). Accepts <span className="font-mono">.csv</span> and <span className="font-mono">.xlsx</span>.
            </p>
            <label
              className={`block border-2 border-dashed rounded-xl px-6 py-10 text-center cursor-pointer transition ${uploading ? 'border-indigo-400 bg-indigo-50/40' : 'border-gray-300 hover:border-indigo-400 hover:bg-indigo-50/30'}`}
              data-testid="bulk-ea-sheet-drop"
            >
              <input
                type="file"
                accept=".csv,.xlsx,.xlsm"
                onChange={handleFile}
                disabled={uploading}
                className="sr-only"
                data-testid="bulk-ea-sheet-input"
              />
              {uploading ? (
                <div className="inline-flex items-center gap-2 text-indigo-700 text-sm">
                  <CircleNotch size={18} className="animate-spin" />
                  Parsing {file?.name || 'sheet'}…
                </div>
              ) : (
                <>
                  <UploadSimple size={28} className="mx-auto text-gray-500 mb-2" weight="duotone" />
                  <div className="text-[13px] font-medium text-gray-800">Click to choose a CSV or Excel file</div>
                  <div className="text-[11px] text-gray-500 mt-1">Up to 500 students • 5 MB max</div>
                </>
              )}
            </label>
          </div>
        )}

        {/* ============= PREVIEW PHASE ============= */}
        {phase === 'preview' && match && (
          <div className="py-2 space-y-4">
            <div className={`grid ${(match.counts.already_sent || 0) > 0 ? 'grid-cols-4' : 'grid-cols-3'} gap-2 text-center`}>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 py-3 px-2">
                <div className="text-2xl font-bold text-emerald-700 tabular-nums" data-testid="bulk-ea-sheet-matched-count">{match.counts.matched}</div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-emerald-800 mt-0.5">Ready to send</div>
              </div>
              {(match.counts.already_sent || 0) > 0 && (
                <div className="rounded-lg border border-sky-200 bg-sky-50 py-3 px-2">
                  <div className="text-2xl font-bold text-sky-700 tabular-nums" data-testid="bulk-ea-sheet-already-sent-count">{match.counts.already_sent}</div>
                  <div className="text-[10.5px] font-semibold uppercase tracking-wider text-sky-800 mt-0.5">Already sent</div>
                </div>
              )}
              <div className="rounded-lg border border-amber-200 bg-amber-50 py-3 px-2">
                <div className="text-2xl font-bold text-amber-700 tabular-nums">
                  {match.counts.ambiguous}
                  {Object.values(verifiedRows).filter(Boolean).length > 0 && (
                    <span className="text-[13px] text-emerald-700 font-semibold ml-1.5" data-testid="bulk-ea-sheet-verified-count">
                      (+{Object.values(verifiedRows).filter(Boolean).length} ✓)
                    </span>
                  )}
                </div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-amber-800 mt-0.5">Ambiguous</div>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50 py-3 px-2">
                <div className="text-2xl font-bold text-red-700 tabular-nums">{match.counts.unmatched}</div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-red-800 mt-0.5">Unmatched</div>
              </div>
            </div>

            <div className="text-[11.5px] text-gray-500 leading-relaxed">
              <span className="font-semibold text-emerald-700">Ready to send</span> students are auto-selected. {(match.counts.already_sent || 0) > 0 && (<><span className="font-semibold text-sky-700">Already sent</span> students are skipped automatically — no re-sending. </>)}For <span className="font-semibold text-amber-700">Ambiguous</span> rows (usually name typos), tick the checkbox on any you can visually confirm — they'll be added to the send batch.
            </div>

            <BucketRow
              label="Ready to send"
              tone="emerald"
              open={expandedList === 'matched'}
              onToggle={() => setExpandedList(expandedList === 'matched' ? null : 'matched')}
              count={match.counts.matched}
              rows={match.matched}
              renderRow={r => (
                <div key={`${r.row_number}-${r.student_id}`} className="flex items-center justify-between gap-3 py-1.5 text-[12px]">
                  <div className="flex items-center gap-2 min-w-0">
                    <CheckCircle size={13} weight="fill" className="text-emerald-500 shrink-0" />
                    <span className="truncate font-medium text-gray-900">{r.student_name}</span>
                  </div>
                  <span className="text-gray-500 text-[11px] truncate max-w-[180px]">{r.student_email}</span>
                </div>
              )}
            />

            {(match.counts.already_sent || 0) > 0 && (
              <BucketRow
                label="Already sent — will be skipped"
                tone="sky"
                open={expandedList === 'already_sent'}
                onToggle={() => setExpandedList(expandedList === 'already_sent' ? null : 'already_sent')}
                count={match.counts.already_sent}
                rows={match.already_sent || []}
                renderRow={r => (
                  <div key={`${r.row_number}-${r.student_id}`} className="flex items-center justify-between gap-3 py-1.5 text-[12px]" data-testid={`bulk-ea-sheet-already-sent-row-${r.student_id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <CheckCircle size={13} weight="fill" className="text-sky-500 shrink-0" />
                      <span className="truncate font-medium text-gray-900">{r.student_name}</span>
                      <span className="text-[10px] text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded uppercase tracking-wide font-semibold whitespace-nowrap">
                        {(r.application_stage || 'EA sent').replaceAll('_', ' ')}
                      </span>
                    </div>
                    <span className="text-gray-500 text-[11px] truncate max-w-[180px]">{r.student_email}</span>
                  </div>
                )}
              />
            )}

            <BucketRow
              label="Ambiguous — please verify"
              tone="amber"
              open={expandedList === 'ambiguous'}
              onToggle={() => setExpandedList(expandedList === 'ambiguous' ? null : 'ambiguous')}
              count={match.counts.ambiguous}
              rows={match.ambiguous}
              renderRow={r => {
                const rowKey = r.row_number;
                const candidates = r.candidates || [];
                const verifiedId = verifiedRows[rowKey];
                const isChecked = !!verifiedId;
                // If exactly one candidate, tick auto-selects it. If 2+ we
                // still auto-pick the first (the closest match by our
                // ranking) but expose a dropdown so registrar can switch.
                const toggle = () => {
                  setVerifiedRows(prev => {
                    const next = { ...prev };
                    if (isChecked) {
                      delete next[rowKey];
                    } else if (candidates[0]?.student_id) {
                      next[rowKey] = candidates[0].student_id;
                    }
                    return next;
                  });
                };
                const pickCandidate = (sid) => {
                  setVerifiedRows(prev => ({ ...prev, [rowKey]: sid }));
                };
                return (
                  <div key={rowKey} className="py-1.5 text-[12px] space-y-1 border-b border-amber-100 last:border-0">
                    <div className="flex items-center gap-2">
                      {candidates.length > 0 ? (
                        <input
                          type="checkbox"
                          className="w-3.5 h-3.5 rounded border-amber-400 accent-amber-600"
                          checked={isChecked}
                          onChange={toggle}
                          data-testid={`bulk-ea-verify-row-${rowKey}`}
                          title="Tick if this really is the same student despite the name/email typo"
                        />
                      ) : (
                        <WarningCircle size={13} weight="fill" className="text-amber-500 shrink-0" />
                      )}
                      <span className="font-medium text-gray-900">Row {rowKey}</span>
                      <span className="text-gray-500">— {r.sheet_name}</span>
                      <span className="text-gray-400">·</span>
                      <span className="text-gray-500 truncate">{r.sheet_email}</span>
                    </div>
                    <div className="pl-6 text-[11px] text-amber-700">{r.reason}</div>
                    {candidates.length === 1 && (
                      <div className="pl-6 text-[11px] text-gray-500">
                        {isChecked ? (
                          <span className="text-emerald-700 font-medium">
                            <CheckCircle size={11} weight="fill" className="inline mr-1 mb-0.5" />
                            Will send to{' '}
                            <button
                              type="button"
                              onClick={() => openCandidatePreview(candidates[0].student_id)}
                              className="font-semibold underline decoration-dotted underline-offset-2 hover:text-emerald-900"
                              data-testid={`bulk-ea-candidate-name-${rowKey}`}
                              title="Click to preview this student's profile"
                            >
                              {candidates[0].student_name}
                            </button>
                            {' · '}{candidates[0].student_email}
                          </span>
                        ) : (
                          <>
                            Closest match:{' '}
                            <button
                              type="button"
                              onClick={() => openCandidatePreview(candidates[0].student_id)}
                              className="text-gray-800 font-medium underline decoration-dotted underline-offset-2 hover:text-gray-900"
                              data-testid={`bulk-ea-candidate-name-${rowKey}`}
                              title="Click to preview this student's profile before ticking"
                            >
                              {candidates[0].student_name}
                            </button>
                            {' · '}{candidates[0].student_email}
                          </>
                        )}
                      </div>
                    )}
                    {candidates.length > 1 && (
                      <div className="pl-6 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[11px] text-gray-500 shrink-0">Send to:</span>
                          <select
                            className="text-[11px] border border-gray-300 rounded px-1.5 py-0.5 bg-white disabled:opacity-50"
                            value={verifiedId || ''}
                            disabled={!isChecked}
                            onChange={(e) => pickCandidate(e.target.value)}
                            data-testid={`bulk-ea-verify-candidate-${rowKey}`}
                          >
                            {!isChecked && <option value="">— tick to verify —</option>}
                            {candidates.map(c => (
                              <option key={c.student_id} value={c.student_id}>
                                {c.student_name} · {c.student_email}
                              </option>
                            ))}
                          </select>
                          {isChecked && verifiedId && (
                            <button
                              type="button"
                              onClick={() => openCandidatePreview(verifiedId)}
                              className="text-[11px] text-indigo-700 hover:text-indigo-900 underline decoration-dotted underline-offset-2"
                              data-testid={`bulk-ea-candidate-preview-${rowKey}`}
                            >
                              View profile
                            </button>
                          )}
                        </div>
                        {!isChecked && (
                          <div className="text-[11px] text-gray-500 space-x-2">
                            <span>Candidates:</span>
                            {candidates.map((c, i) => (
                              <button
                                key={c.student_id}
                                type="button"
                                onClick={() => openCandidatePreview(c.student_id)}
                                className="text-gray-800 font-medium underline decoration-dotted underline-offset-2 hover:text-gray-900"
                              >
                                {c.student_name}{i < candidates.length - 1 ? ',' : ''}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {/* Inline profile preview — expands under the row when a
                        candidate name is clicked. Only one at a time to keep
                        the modal from ballooning past a laptop viewport. */}
                    {previewCandidateId && candidates.some(c => c.student_id === previewCandidateId) && (
                      <CandidatePreviewCard
                        entry={candidateProfiles[previewCandidateId]}
                        onClose={() => setPreviewCandidateId(null)}
                      />
                    )}
                  </div>
                );
              }}
            />

            <BucketRow
              label="Unmatched — not in your system"
              tone="red"
              open={expandedList === 'unmatched'}
              onToggle={() => setExpandedList(expandedList === 'unmatched' ? null : 'unmatched')}
              count={match.counts.unmatched}
              rows={match.unmatched}
              renderRow={r => (
                <div key={r.row_number} className="flex items-center gap-2 py-1.5 text-[12px]">
                  <XCircle size={13} weight="fill" className="text-red-500 shrink-0" />
                  <span className="font-medium text-gray-900">Row {r.row_number}</span>
                  <span className="text-gray-500">— {r.sheet_name}</span>
                  <span className="text-gray-400">·</span>
                  <span className="text-gray-500">{r.sheet_email}</span>
                </div>
              )}
            />

            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              <Button variant="outline" size="sm" onClick={reset} className="text-[12px]" data-testid="bulk-ea-sheet-back-btn">
                <ArrowLeft size={13} className="mr-1.5" /> Choose different file
              </Button>
              {(() => {
                const verifiedCount = Object.values(verifiedRows).filter(Boolean).length;
                const total = match.counts.matched + verifiedCount;
                return (
                  <Button
                    size="sm"
                    onClick={handleSend}
                    disabled={!total}
                    className="bg-indigo-700 hover:bg-indigo-800 text-white text-[12px] font-medium px-5"
                    data-testid="bulk-ea-sheet-send-btn"
                    title={verifiedCount ? `${match.counts.matched} matched + ${verifiedCount} manually verified` : `${match.counts.matched} matched`}
                  >
                    <PaperPlaneRight size={13} className="mr-1.5" weight="fill" /> Send Bulk EA ({total})
                  </Button>
                );
              })()}
            </div>
          </div>
        )}

        {/* ============= SENDING PHASE ============= */}
        {phase === 'sending' && (
          <div className="py-8 text-center space-y-4">
            <CircleNotch size={32} className="mx-auto text-indigo-600 animate-spin" />
            <div className="text-[13px] font-medium text-gray-800">
              Sending {sendJob?.total || match?.counts.matched || 0} EA{(sendJob?.total || 0) === 1 ? '' : 's'}…
            </div>
            {sendJob && (
              <div className="max-w-md mx-auto">
                <div className="flex items-center justify-between text-[11px] font-medium text-gray-600 mb-1.5">
                  <span>Sent</span>
                  <span className="tabular-nums">{sendJob.done || 0} / {sendJob.total || 0}</span>
                </div>
                <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                  <div className="h-full bg-indigo-600 transition-all duration-500" style={{ width: `${Math.round(((sendJob.done || 0) / (sendJob.total || 1)) * 100)}%` }} />
                </div>
                {sendJob.counts && (
                  <div className="mt-3 flex items-center justify-center gap-4 text-[11px] text-gray-600">
                    <span className="inline-flex items-center gap-1"><CheckCircle size={11} weight="fill" className="text-emerald-500" />{sendJob.counts.sent || 0} sent</span>
                    <span className="inline-flex items-center gap-1"><WarningCircle size={11} weight="fill" className="text-amber-500" />{sendJob.counts.skipped || 0} skipped</span>
                    {(sendJob.counts.errors || 0) > 0 && (
                      <span className="inline-flex items-center gap-1"><XCircle size={11} weight="fill" className="text-red-500" />{sendJob.counts.errors} errors</span>
                    )}
                  </div>
                )}
                <p className="mt-3 text-[10.5px] text-gray-400">Safe to keep this tab open. This runs on the server.</p>
                {sendJob.chunk_total > 1 && (
                  <p className="mt-1 text-[10.5px] text-gray-500 tabular-nums" data-testid="bulk-ea-chunk-progress">
                    Batch <span className="font-semibold text-gray-700">{sendJob.chunk_index || 0}</span> of <span className="font-semibold text-gray-700">{sendJob.chunk_total}</span> (10 students per batch)
                  </p>
                )}
                {sendJob.current_student && (
                  <p className="mt-1 text-[10.5px] text-gray-500 truncate" data-testid="bulk-ea-current-student">
                    Currently processing: <span className="font-medium text-gray-700">{sendJob.current_student}</span>
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* ============= DONE PHASE ============= */}
        {phase === 'done' && sendJob && (
          <div className="py-4 space-y-4">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 py-3 px-2">
                <div className="text-2xl font-bold text-emerald-700 tabular-nums" data-testid="bulk-ea-sheet-sent-count">{sendJob.counts?.sent || 0}</div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-emerald-800 mt-0.5">EAs Sent</div>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 py-3 px-2">
                <div className="text-2xl font-bold text-amber-700 tabular-nums">{sendJob.counts?.skipped || 0}</div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-amber-800 mt-0.5">Skipped</div>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50 py-3 px-2">
                <div className="text-2xl font-bold text-red-700 tabular-nums">{sendJob.counts?.errors || 0}</div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-red-800 mt-0.5">Errors</div>
              </div>
            </div>

            {(sendJob.results || []).filter(r => !r.ok).length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 max-h-56 overflow-y-auto">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-800 mb-2">Skipped / Errored</div>
                {(sendJob.results || []).filter(r => !r.ok).map(r => (
                  <div key={r.student_id} className="flex items-start gap-2 py-1 text-[12px]">
                    {r.skipped ? <WarningCircle size={13} weight="fill" className="text-amber-500 shrink-0 mt-0.5" /> : <XCircle size={13} weight="fill" className="text-red-500 shrink-0 mt-0.5" />}
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900">{r.student_name || r.student_id}</div>
                      <div className="text-[11px] text-gray-600">{r.reason}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-end pt-3 border-t border-gray-100">
              <Button onClick={handleClose} size="sm" className="bg-gray-900 hover:bg-black text-white text-[12px] px-5" data-testid="bulk-ea-sheet-close-btn">
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function BucketRow({ label, tone, count, rows, renderRow, open, onToggle }) {
  const toneClasses = {
    emerald: 'border-emerald-200 hover:bg-emerald-50/50',
    amber: 'border-amber-200 hover:bg-amber-50/50',
    red: 'border-red-200 hover:bg-red-50/50',
    sky: 'border-sky-200 hover:bg-sky-50/50',
  }[tone];
  return (
    <div className={`rounded-lg border ${toneClasses.split(' ')[0]}`}>
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-3 py-2 transition ${toneClasses}`}
        disabled={!count}
      >
        <div className="flex items-center gap-2">
          {open ? <CaretDown size={13} weight="bold" /> : <CaretRight size={13} weight="bold" />}
          <span className="text-[12px] font-semibold text-gray-800">{label}</span>
        </div>
        <span className={`inline-flex items-center justify-center min-w-[24px] h-5 px-1.5 rounded-full text-[11px] font-bold ${count ? 'bg-white' : 'bg-gray-100 text-gray-400'} border ${toneClasses.split(' ')[0]}`}>{count}</span>
      </button>
      {open && count > 0 && (
        <div className="border-t border-gray-100 px-3 py-2 max-h-52 overflow-y-auto space-y-0.5">
          {rows.map(renderRow)}
        </div>
      )}
    </div>
  );
}


/**
 * Compact identity card for the "click name → preview profile" flow.
 * Renders exactly the fields a registrar needs to decide "yes, this is the
 * same person" — name, DOB, email, intake, program, recruiter, and a count
 * of uploaded docs (with the passport filename if we have it). Everything
 * else on the student record is either noise for this decision or PII we
 * shouldn't parade around unnecessarily.
 */
function CandidatePreviewCard({ entry, onClose }) {
  if (!entry) return null;
  if (entry.state === 'loading') {
    return (
      <div className="mt-2 ml-6 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-[11px] text-gray-500 inline-flex items-center gap-2" data-testid="bulk-ea-candidate-preview-loading">
        <CircleNotch size={12} className="animate-spin" /> Loading profile…
      </div>
    );
  }
  if (entry.state === 'error') {
    return (
      <div className="mt-2 ml-6 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700 flex items-center justify-between gap-2">
        <span><WarningCircle size={12} weight="fill" className="inline mr-1 mb-0.5" />{entry.error}</span>
        <button type="button" onClick={onClose} className="text-red-500 hover:text-red-700"><X size={12} /></button>
      </div>
    );
  }
  const p = entry.profile || {};
  const files = p.files || [];
  const passport = files.find(f => (f.document_type || '').toLowerCase().includes('passport'));
  return (
    <div
      className="mt-2 ml-6 rounded-lg border border-indigo-200 bg-indigo-50/40 px-3 py-3 text-[11.5px] space-y-2"
      data-testid="bulk-ea-candidate-preview-card"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-gray-900 flex items-center gap-1.5">
            <User size={12} weight="fill" className="text-indigo-600" />
            {p.full_name || '—'}
          </div>
          <div className="text-[10.5px] text-gray-500 mt-0.5">{p.email || '—'}</div>
        </div>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 shrink-0" title="Close preview">
          <X size={12} />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
        <ProfileField icon={Calendar} label="Date of Birth" value={p.dob || p.date_of_birth || '—'} />
        <ProfileField icon={GraduationCap} label="Program" value={p.program_name || p.assigned_program_name || p.program || '—'} />
        <ProfileField icon={Calendar} label="Intake" value={p.intake_label || p.intake_date || '—'} />
        <ProfileField icon={User} label="Recruiter" value={p.recruiter_name || p.recruiter_email || 'Direct'} />
        <ProfileField icon={User} label="Stage" value={p.stage_label || p.application_stage || '—'} />
        <ProfileField icon={Files} label="Documents" value={files.length > 0 ? `${files.length} uploaded${passport ? ` (incl. passport)` : ''}` : 'None uploaded'} />
      </div>
      {(p.address_line1 || p.city) && (
        <div className="text-[10.5px] text-gray-600 border-t border-indigo-100 pt-1.5">
          <span className="text-gray-400 uppercase tracking-wider text-[9.5px] font-semibold">Address · </span>
          {[p.address_line1, p.address_line2, p.city, p.province, p.postal_code].filter(Boolean).join(', ')}
        </div>
      )}
    </div>
  );
}


function ProfileField({ icon: Icon, label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-[9.5px] font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-1">
        <Icon size={9} /> {label}
      </div>
      <div className="text-gray-800 truncate" title={typeof value === 'string' ? value : ''}>{value}</div>
    </div>
  );
}
